<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  status: {
    type: String,
    default: '',
  },
  proposedCount: {
    type: Number,
    default: null,
  },
  isCancelling: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['cancel']);

const { t } = useI18n();

const isRunning = computed(() => props.status === 'running');
const hasFailed = computed(() => props.status === 'failed');

// An analysis that finished without finding a pattern used to leave the screen looking
// exactly like one that never ran. Say so.
const foundNothing = computed(
  () => props.status === 'completed' && props.proposedCount === 0
);

const isVisible = computed(
  () => isRunning.value || hasFailed.value || foundNothing.value
);
</script>

<template>
  <div
    v-show="isVisible"
    class="flex items-start gap-3 p-4 mt-7 border rounded-xl"
    :class="
      hasFailed
        ? 'border-n-ruby-6 bg-n-ruby-2'
        : 'border-n-slate-5 bg-n-alpha-2'
    "
  >
    <Spinner
      v-if="isRunning"
      :size="16"
      class="flex-shrink-0 mt-1 text-n-slate-11"
    />
    <div class="flex flex-col flex-1 gap-1">
      <span class="text-sm font-medium text-n-slate-12">
        {{
          isRunning
            ? t('CAPTAIN.ASSISTANTS.SCENARIOS.ANALYSIS.RUNNING_TITLE')
            : hasFailed
              ? t('CAPTAIN.ASSISTANTS.SCENARIOS.ANALYSIS.FAILED_TITLE')
              : t('CAPTAIN.ASSISTANTS.SCENARIOS.ANALYSIS.EMPTY_TITLE')
        }}
      </span>
      <span class="text-sm text-n-slate-11">
        {{
          isRunning
            ? t('CAPTAIN.ASSISTANTS.SCENARIOS.ANALYSIS.RUNNING_BODY')
            : hasFailed
              ? t('CAPTAIN.ASSISTANTS.SCENARIOS.ANALYSIS.FAILED_BODY')
              : t('CAPTAIN.ASSISTANTS.SCENARIOS.ANALYSIS.EMPTY_BODY')
        }}
      </span>
    </div>
    <Button
      v-if="isRunning"
      type="button"
      :label="t('CAPTAIN.ASSISTANTS.SCENARIOS.ANALYSIS.CANCEL')"
      :is-loading="isCancelling"
      :disabled="isCancelling"
      class="flex-shrink-0"
      faded
      slate
      sm
      @click="emit('cancel')"
    />
  </div>
</template>
